import { Component, Vue } from "vue-property-decorator";
import moment from "moment";
import { cprsEndorsementApplicationApi } from "@/providers/apis/cprs-endorsement-application";
import {
  CprsEndorsementPayload,
  DropdownOption,
} from "@/models/cprs-endorsement-application/cprs-endorsement-application.interface";
import { User } from "@/models/common-interface/common.interface";
import { loginApi } from "@/providers/apis/login";
import { utils } from "@/utils/utils";
import EligibilityPopupComponent from "../eligibility-qsn-valid-popup/eligibility-qsn-valid-popup.vue";
import AppLoaderComponent from "../app-loader/app-loader.component.vue";

@Component({
  components: {
    EligibilityPopupComponent,
    AppLoaderComponent,
  },
})
export default class CprsEndorsementApplicationComponent extends Vue {
  public isLoading = false;
  public errorMessage = "";
  public userDetails: User = {} as User;

  public payLoadData: CprsEndorsementPayload = {
    fillStatus: "DRAFT",
    id: "",
    cprsJBasicDetails: {
      firstName: "",
      lastName: "",
      addressLine1: "",
      city: "",
      state: "",
      email: "",
      phoneNumber: "",
      preferredName: "",
      cprsCertificateNumber: "",
    },
    cprsJQuestions: {
      justiceSystemInvolvement: "",
      willingToSelfIdentify: "",
      whyCprsJ: "",
    },
    cprsJSignature: {
      isSignature: "",
      submissionDate: "",
    },
  };

  // Blocks the form when the applicant already has a CPRS-J application that is
  // still in play; isCheckingExisting keeps the form hidden until we know.
  public isCheckingExisting = true;
  public alreadyApplied = false;
  public existingStatus = "";

  // Show vee-validate errors only after the first submit attempt.
  public checkValidForm = false;
  // Signature pad is validated manually (not a vee-validate field).
  public signatureInvalid = false;

  public yesNoOptions: DropdownOption[] = [
    { label: "Yes", value: "Yes" },
    { label: "No", value: "No" },
  ];

  public async created() {
    this.userDetails = utils.getUserDetails();
    const token = localStorage.getItem("access_token");
    if (!token || !this.userDetails) {
      this.$router.push({
        name: "login-page",
        query: { application: "endorsement" },
      });
      return;
    }

    // Applicant-only form — reviewers/admins (non-empty roles) are sent back to
    // their dashboard instead of seeing the application form.
    if (
      Array.isArray(this.userDetails.roles) &&
      this.userDetails.roles.length
    ) {
      this.$router.replace("/reviewer-dashboard/home");
      return;
    }

    // One CPRS-J application per applicant. A previous submission blocks a new
    // one unless it came back Not Accepted, in which case they may reapply.
    await this.checkExistingApplication();
    if (this.alreadyApplied) {
      this.isCheckingExisting = false;
      return;
    }
    this.isCheckingExisting = false;

    // Nothing is restored from a previous visit: the form is always seeded from
    // whoever is signed in right now, impersonated or not.
    await this.prefillFromProfile();

    // The date is display-only, so it is always today rather than whatever was
    // saved: a draft started yesterday must not submit yesterday's date.
    this.payLoadData.cprsJSignature.submissionDate = this.todayIso();
  }

  /**
   * Set when the existing-application lookup failed for a reason other than
   * 404. The applicant is still let through; this only records that the check
   * did not actually run, so the state is not mistaken for "no application".
   */
  public existingLookupFailed = false;

  // A submitted CPRS-J application bars a second one until it is Not Accepted.
  // Drafts do not count — the applicant is still finishing their first attempt.
  private async checkExistingApplication() {
    const email = this.userDetails?.email;
    if (!email) {
      return;
    }
    try {
      const response =
        await cprsEndorsementApplicationApi.getCprsEndorsementApplicationByEmail(
          email
        );
      const body = response?.data;
      const records = Array.isArray(body) ? body : [body];
      const blocking = records.find(
        (record: any) =>
          record &&
          record.fillStatus === "COMPLETED" &&
          String(record.progressStatus || "").toUpperCase() !== "NOT ACCEPTED"
      );
      if (blocking) {
        this.alreadyApplied = true;
        this.existingStatus = blocking.progressStatus || "";
      }
    } catch (error: any) {
      // 404 is the ordinary case, not a failure: this applicant has never
      // applied, so there is nothing to block them. Anything else is a real
      // fault, but we still let them through rather than locking out a
      // first-time applicant over it — the backend remains the authority at
      // submit time.
      if (error?.response?.status !== 404) {
        this.existingLookupFailed = true;
      }
    }
  }

  // Reaching this form requires a session, so the applicant's own details are
  // already known — fill them in rather than making them retype. Only blanks
  // are filled, so a restored draft and anything already typed both win.
  private async prefillFromProfile() {
    // The session copy is a trimmed user object: it carries name and email but
    // not the address, city, state or phone the form asks for. /user/details
    // returns the full record, already mapped into the same camelCase shape,
    // so fetch it and fall back to the session copy if the call fails.
    let user: any = this.userDetails;
    try {
      const profile: any = await loginApi.getUser();
      if (profile) {
        user = { ...user, ...profile };
      }
    } catch {
      // Keep the session copy — a partial prefill beats an empty form.
    }
    if (!user) {
      return;
    }
    const basic: any = this.payLoadData.cprsJBasicDetails;
    const seed: any = {
      firstName: user.firstName,
      lastName: user.lastName,
      addressLine1: user.addressLine1,
      city: user.city,
      // The State input is a 2-letter field and upper-cases on entry.
      state: user.state ? String(user.state).toUpperCase() : "",
      email: user.email,
      phoneNumber: user.phoneNumber,
    };
    Object.keys(seed).forEach((key: string) => {
      if (!basic[key] && seed[key]) {
        basic[key] = seed[key];
      }
    });
  }

  // Submission date shown to the applicant; the payload keeps the ISO value.
  public get submissionDateDisplay(): string {
    const date = this.payLoadData.cprsJSignature.submissionDate;
    return date ? moment(date).format("MMM DD, YYYY") : "";
  }

  private todayIso(): string {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }

  // Capture the drawn signature as base64.
  public onSignatureEnd() {
    const pad: any = this.$refs.signaturePad;
    if (pad) {
      const { isEmpty, data } = pad.saveSignature();
      if (!isEmpty) {
        this.payLoadData.cprsJSignature.isSignature = data;
        this.signatureInvalid = false;
      }
    }
  }

  public clearSignature() {
    const pad: any = this.$refs.signaturePad;
    if (pad) {
      pad.clearSignature();
    }
    this.payLoadData.cprsJSignature.isSignature = "";
  }

  private extractErrorMessage(error: any, fallback: string): string {
    const data = error?.response?.data;
    if (!data) return fallback;
    if (typeof data === "string") return data;
    if (data.message) return data.message;
    if (data.error) return data.error;
    return fallback;
  }

  // Dismiss the alert popup and clear the message.
  public closdedErrorPopup() {
    this.errorMessage = "";
  }

  public goToDashboard() {
    this.$router.push("/user-dashboard/home");
  }

  public onSubmit() {
    this.errorMessage = "";
    this.checkValidForm = true;
    this.signatureInvalid = !this.payLoadData.cprsJSignature.isSignature;
    this.$validator.validateAll().then((valid: boolean) => {
      if (!valid || this.signatureInvalid) {
        // Scroll to the first visible validation error, not the top.
        this.$nextTick(() => {
          const el = document.querySelector(".field-error") as HTMLElement;
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        });
        return;
      }
      this.isLoading = true;
      this.payLoadData.fillStatus = "COMPLETED";
      cprsEndorsementApplicationApi
        .submitCprsEndorsementApplication(this.payLoadData, "COMPLETED")
        .then((result: any) => {
          // The POST response body carries the backend-generated acknowledgement
          // code, so no second fetch-by-id is needed.
          this.isLoading = false;
          this.$router.push({
            path: "/form/submitted",
            query: {
              subTitle: "Endorsement Application",
              applicationId: result?.data?.code,
            },
          });
        })
        .catch((error: any) => {
          this.isLoading = false;
          this.errorMessage = this.extractErrorMessage(
            error,
            "Something went wrong while submitting. Please try again."
          );
        });
    });
  }
}
